import { HugeiconsIcon, type HugeiconsIconProps } from "@hugeicons/react";
import {
	AiAudioIcon,
	CheckmarkCircle02Icon,
	CircleArrowRight01Icon,
	DashedLineCircleIcon,
	Mic01DuotoneRounded,
	StartUp02Icon,
	UserMultiple02Icon,
} from "@hugeicons-pro/core-duotone-rounded";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Link,
	type LinkProps,
	useNavigate,
} from "@tanstack/react-router";
import { useState } from "react";
import { ChannelTeamDialog } from "~/components/channel-team-dialog";
import { AudioSettingsDialog } from "~/components/dialogs/audio-settings";
import { EpisodeUploadDialog } from "~/components/episode-upload-dialog";
import { AppContainer } from "~/components/layouts/app/app-container";
import { Button } from "~/components/ui/button";
import {
	FormCard,
	FormCardBody,
	FormCardFooter,
	FormCardHeader,
	FormCardHeaderSubtitle,
	FormCardHeaderTitle,
} from "~/components/ui/form-card";
import { Label } from "~/components/ui/label";
import { PageHead } from "~/components/ui/page-head";
import { stopOnboarding } from "~/functions/onboarding";
import { cn } from "~/lib/utils";
import {
	canAddEpisodesToChannel,
	isChannelManagedExternally,
} from "~/models/channel";
import { useTRPC } from "~/trpc/client";

export const Route = createFileRoute("/_app/podcasts/$channelUid/onboarding")({
	component: Page,
});

function Page() {
	const { channelUid } = Route.useParams();
	const trpc = useTRPC();
	const { t } = useLingui();
	const navigate = useNavigate();

	const { data: channel } = useSuspenseQuery(
		trpc.channel.find.queryOptions({ channelUid }),
	);
	const { data: episodes } = useQuery(
		trpc.episode.list.queryOptions({ channelUid }, { refetchInterval: 5_000 }),
	);
	const { data: permissions } = useQuery(
		trpc.channel.listPermissions.queryOptions({
			channelUid,
		}),
	);

	const [isOpenUploadDialog, setIsOpenUploadDialog] = useState(false);
	const [isOpenTeamDialog, setIsOpenTeamDialog] = useState(false);
	const [isOpenAudioSettingsDialog, setIsOpenAudioSettingsDialog] =
		useState(false);

	const isChannelExternal = isChannelManagedExternally(channel);

	const checkList: Array<CheckListItem> = [
		isChannelExternal
			? {
					key: "episodes",
					icon: Mic01DuotoneRounded,
					title: t`Import Episodes`,
					description: t`We're importing and transcoding all your existing episodes in our platform. This operation may take some time.`,
					status:
						!!episodes &&
						episodes.length > 0 &&
						episodes.some((episode) => episode.status === "Transcoding")
							? "ongoing"
							: "complete",
				}
			: {
					key: "episodes",
					icon: Mic01DuotoneRounded,
					title: t`Create your first episode`,
					description: t`Get started by creating your first episode.`,
					status:
						!!episodes && episodes.length > 0
							? episodes.some((episode) => episode.status === "Transcoding")
								? "ongoing"
								: "complete"
							: "todo",
					onClick: () => {
						setIsOpenUploadDialog(true);
					},
				},
		{
			key: "team",
			icon: UserMultiple02Icon,
			title: t`Invite Team Members`,
			description: t`Collaborate with your team by inviting them to your podcast channel.`,
			status: permissions && permissions.length > 1 ? "complete" : "todo",
			onClick: () => {
				setIsOpenTeamDialog(true);
			},
		},
		{
			key: "audio",
			icon: AiAudioIcon,
			title: t`Audio Settings`,
			description: t`Set your preferences regarding normalization, audio format & bitrate.`,
			status: "todo",
			onClick: () => {
				setIsOpenAudioSettingsDialog(true);
			},
		},
	];

	async function skipOnboarding() {
		await stopOnboarding({ data: channelUid });

		navigate({
			to: "/podcasts/$channelUid/statistics",
			params: { channelUid },
		});
	}

	return (
		<>
			<PageHead
				icon={StartUp02Icon}
				title={t`Podcast Onboarding`}
				subtitle={t`Let's get started with your podcast journey!`}
			/>
			<AppContainer className="my-12">
				<FormCard className="max-w-xl mx-auto">
					<FormCardHeader className="relative">
						<div>
							<FormCardHeaderTitle>
								<Trans>Onboarding checklist</Trans>
							</FormCardHeaderTitle>
							<FormCardHeaderSubtitle>
								<Trans>
									Congratulations for creating your podcast, let's see what your
									next steps are.
								</Trans>
							</FormCardHeaderSubtitle>
						</div>
					</FormCardHeader>
					<FormCardBody className="relative px-3 pt-4 sm:px-6 sm:py-3">
						<section className="space-y-3">
							{checkList.map((checkItem) => (
								<CheckListItem key={checkItem.key} item={checkItem} />
							))}
						</section>
					</FormCardBody>

					<FormCardFooter className="flex justify-end items-center">
						<Button variant="outline" onClick={skipOnboarding}>
							<Trans>Skip</Trans>
						</Button>
					</FormCardFooter>
				</FormCard>
				<EpisodeUploadDialog
					canAddEpisodes={canAddEpisodesToChannel(channel)}
					channel={channel}
					isOpen={isOpenUploadDialog}
					setIsOpen={setIsOpenUploadDialog}
					redirect={false}
				/>
				<ChannelTeamDialog
					channel={channel}
					isOpen={isOpenTeamDialog}
					setIsOpen={setIsOpenTeamDialog}
				/>
				<AudioSettingsDialog
					channel={channel}
					isOpen={isOpenAudioSettingsDialog}
					setIsOpen={setIsOpenAudioSettingsDialog}
				/>
			</AppContainer>
		</>
	);
}

interface CheckListItem {
	key: string;
	icon: HugeiconsIconProps["icon"];
	title: string;
	description: string;
	status: "todo" | "complete" | "ongoing";
	link?: LinkProps;
	onClick?: () => void;
}

function CheckListItem({ item }: { item: CheckListItem }) {
	if (item.link) {
		return (
			<Link
				{...item.link}
				className="grid gap-2 hover:bg-muted sm:-mx-3 px-3 py-3 rounded-lg"
			>
				<CheckListItemContent item={item} />
			</Link>
		);
	}

	if (item.onClick) {
		return (
			// biome-ignore lint/a11y/noStaticElementInteractions: intentional exception
			// biome-ignore lint/a11y/useKeyWithClickEvents: intentional exception
			<div
				onClick={item.onClick}
				className="grid gap-2 hover:bg-muted sm:-mx-3 px-3 py-3 rounded-lg cursor-pointer"
			>
				<CheckListItemContent item={item} />
			</div>
		);
	}

	return (
		<div className="grid gap-2 hover:bg-muted sm:-mx-3 px-3 py-3 rounded-lg">
			<CheckListItemContent item={item} />
		</div>
	);
}

function CheckListItemContent({ item }: { item: CheckListItem }) {
	return (
		<div className="flex gap-3 items-center">
			<div className="border border-primary/60 bg-primary-foreground rounded-lg p-2">
				<HugeiconsIcon
					icon={item.icon}
					className={cn("shrink-0 text-primary")}
				/>
			</div>
			<div className="flex grow flex-col gap-2">
				<div className="flex-1">
					<div className="flex items-center justify-between">
						<Label className="cursor-pointer">{item.title}</Label>
					</div>
					<div className="mt-1 text-sm text-muted-foreground">
						{item.description}
					</div>
				</div>
			</div>
			<HugeiconsIcon
				icon={checkListIcons[item.status]}
				className={cn(
					"shrink-0",
					item.status === "ongoing" && "text-primary animate-spin-slow",
					item.status === "todo" && "text-current opacity-50",
					item.status === "complete" && "text-primary",
				)}
			/>
		</div>
	);
}

const checkListIcons = {
	todo: CircleArrowRight01Icon,
	complete: CheckmarkCircle02Icon,
	ongoing: DashedLineCircleIcon,
};
